import styles from '@hts/entities/Card/styles/CardPreview/LiveView.module.scss';
import Konva from 'konva';
import { forwardRef, memo, useCallback, useMemo, useRef } from 'react';
import { Image, Layer, Stage } from 'react-konva';
import { mergeRefs } from 'react-merge-refs';
import { useCardContext, useGetCardLiveViewSizes } from '../../libs';
import {
  INewFieldItem,
  IOnChangeContainerSizesData,
  IOnChangeLogoTransformerData,
  IOnRelalculatedByTextAnchorData,
} from '../../types';
import { getMainImageSizes, logoIsEnabledButNotSet } from '../../utils';
import { CardTextField } from './CardTextField';
import { KonvaImage } from './KonvaImage';
import { LogoKonvaImage } from './LogoKonvaImage';

interface IDefaultProps {
  activeTextFieldId: string | null;
  containerRef: React.RefObject<HTMLDivElement>;
}

interface INonExportProps extends IDefaultProps {
  isForExport?: false;
}

interface IForExportProps extends IDefaultProps {
  isForExport: true;
  changeLogoPosition?: null;
}

type LiveViewProps = INonExportProps | IForExportProps;

export const LiveView = memo(
  forwardRef<Konva.Stage, LiveViewProps>((props, ref) => {
    const { activeTextFieldId, isForExport, containerRef } = props;

    const card = useCardContext((state) => state.card);
    const setCard = useCardContext((state) => state.setCard);

    const stageRef = useRef<Konva.Stage | null>(null);

    const mainImg = card?.previewUrl?.image || undefined;

    const localIsEnabledButNotSet = card?.logo ? logoIsEnabledButNotSet(card.logo) : false;

    const { width: mainImageWidth, height: mainImageHeight } = getMainImageSizes(
      card?.imageDimensions,
      mainImg
    );

    const { dimensions, scaleX, scaleY } = useGetCardLiveViewSizes({
      mainImageWidth,
      mainImageHeight,
      containerRef,
    });

    const onChangeLogoTransformerData = useCallback(
      (transformerData: IOnChangeLogoTransformerData) => {
        setCard((prev) => {
          const prevLogo = prev?.logo;

          if (!prevLogo) return prev;

          return { ...prev, logo: { ...prevLogo, ...transformerData } };
        });
      },
      []
    );

    const onDragEndLogo = useCallback((x: number, y: number) => {
      setCard((prev) => {
        const prevLogo = prev?.logo;

        if (!prevLogo) return prev;

        return { ...prev, logo: { ...prevLogo, x, y } };
      });
    }, []);

    const onRelalculatedByTextAnchor = useCallback((data: IOnRelalculatedByTextAnchorData) => {
      setCard((prev) => {
        const prevFields = prev?.fields;

        if (!prevFields) return prev;

        return {
          ...prev,
          fields: prevFields.map((field) => {
            if (field.id === data.id) {
              return {
                ...field,
                recalculatedByTextAnchor: true,
                text_position_x: data.x,
                text_position_y: data.y,
              };
            }

            return field;
          }),
        };
      });
    }, []);

    const onChangeContainerSizes = useCallback((data: IOnChangeContainerSizesData) => {
      setCard((prev) => {
        const prevFields = prev?.fields;

        if (!prevFields) return prev;

        return {
          ...prev,
          fields: prevFields.map((field) => {
            if (field.id === data.id) {
              return { ...field, blockWidth: data.width, blockHeight: data.height };
            }

            return field;
          }),
        };
      });
    }, []);

    const renderInsertedImages = useMemo(() => {
      if (!card) return null;

      return card.insertedImages.map((insertedImage, i) => {
        const { width, height, x, y, rotation, image } = insertedImage;

        return (
          <KonvaImage
            key={`${insertedImage.id}-${i}`}
            imageUrl={image.fileUrl}
            x={x}
            y={y}
            width={width}
            height={height}
            rotation={rotation}
            listening={false}
          />
        );
      });
    }, [card]);

    const content = useMemo(() => {
      const mainImageElement = (
        <Image image={mainImg} width={mainImageWidth} height={mainImageHeight} listening={false} />
      );

      if (card?.secondaryImage) {
        const {
          currentCropWidth,
          currentCropHeight,
          currentCropX,
          currentCropY,
          width,
          height,
          rotation,
          x,
          y,
          is_enabled,
          is_on_top,
          currentImageUrl,
        } = card.secondaryImage;

        const secondaryImage = (
          <KonvaImage
            imageUrl={currentImageUrl || ''}
            x={x}
            y={y}
            width={width}
            height={height}
            rotation={rotation}
            crop={{
              height: currentCropHeight,
              width: currentCropWidth,
              x: currentCropX,
              y: currentCropY,
            }}
            listening={false}
          />
        );

        if (is_on_top) {
          return (
            <>
              {!isForExport && mainImageElement}
              {Boolean(is_enabled && currentImageUrl) && secondaryImage}
              {renderInsertedImages}
            </>
          );
        } else {
          const renderSecondaryImage = Boolean(is_enabled && currentImageUrl && !isForExport);

          return (
            <>
              {renderSecondaryImage && secondaryImage}
              {!isForExport && mainImageElement}
              {renderInsertedImages}
            </>
          );
        }
      } else {
        return (
          <>
            {!isForExport && mainImageElement}
            {renderInsertedImages}
          </>
        );
      }
    }, [
      mainImg,
      mainImageWidth,
      mainImageHeight,
      card?.secondaryImage,
      isForExport,
      renderInsertedImages,
    ]);

    const renderCardTextField = useCallback(
      (item: INewFieldItem, i: number) =>
        card && (
          <CardTextField
            isActive={activeTextFieldId === item.id}
            key={`${item.id}-${i}`}
            item={item}
            index={i}
            defaultHebrewFont={card.defaultHebrewFont}
            defaultEnglishFont={card.defaultEnglishFont}
            isForExport={isForExport}
            onRelalculatedByTextAnchor={onRelalculatedByTextAnchor}
            onChangeContainerSizes={onChangeContainerSizes}
          />
        ),
      [activeTextFieldId, card, isForExport]
    );

    return (
      <Stage
        ref={mergeRefs([stageRef, ref])}
        width={dimensions.width}
        height={dimensions.height}
        scale={{ x: scaleX, y: scaleY }}
        className={styles.container}
      >
        <Layer>
          {content}

          {!isForExport && card?.watermark?.fileUrl ? (
            <KonvaImage
              imageUrl={card.watermark.fileUrl}
              width={mainImageWidth}
              height={mainImageHeight}
              listening={false}
              opacity={0.5}
            />
          ) : null}

          {card?.fields.map(renderCardTextField)}

          {Boolean(card?.logo?.is_enabled && !localIsEnabledButNotSet) && (
            <LogoKonvaImage
              logo={card!.logo!}
              primaryImage={mainImg || undefined}
              isForExport={isForExport}
              onChangeTransformerData={onChangeLogoTransformerData}
              onDragEnd={onDragEndLogo}
            />
          )}
        </Layer>
      </Stage>
    );
  })
);
